
import React, { useState } from 'react';
import { Language, UserRole } from '../types';
import { TRANSLATIONS, LANGUAGE_NAMES } from '../translations';
import { 
  User, Wallet, Bell, Shield, Mail, Globe, CreditCard, 
  LogOut, Save, CheckCircle, Send
} from 'lucide-react';

interface SettingsViewProps {
  language: Language;
  onLanguageChange: () => void;
  userRole: UserRole;
  walletConnected: boolean;
  onDisconnectWallet: () => void;
  onConnectWallet: () => void;
}

export const SettingsView: React.FC<SettingsViewProps> = ({ 
  language, 
  onLanguageChange, 
  userRole,
  walletConnected,
  onDisconnectWallet,
  onConnectWallet
}) => {
  const t = TRANSLATIONS[language];
  const [activeTab, setActiveTab] = useState<'general' | 'wallet' | 'notifications' | 'security'>('general');
  const [saved, setSaved] = useState(false);

  // Mock State
  const [email, setEmail] = useState('user@example.com');
  const [currency, setCurrency] = useState('USDT');
  const [notifications, setNotifications] = useState({
    email: true,
    push: true,
    marketing: false,
    telegram: false // New
  });
  const [telegramHandle, setTelegramHandle] = useState(''); // New
  const [twoFactor, setTwoFactor] = useState(false);

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const tabs = [
    { id: 'general', label: t.generalSettings, icon: User },
    { id: 'wallet', label: t.walletSettings, icon: Wallet },
    { id: 'notifications', label: t.notificationSettings, icon: Bell },
    { id: 'security', label: t.securitySettings, icon: Shield },
  ];

  return (
    <div className="space-y-6 animate-in fade-in duration-300 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-2xl font-bold text-white">{t.settings}</h1>
        {saved && (
          <div className="flex items-center gap-2 text-emerald-400 bg-emerald-500/10 px-4 py-2 rounded-lg border border-emerald-500/20 animate-in fade-in slide-in-from-top-2">
            <CheckCircle size={18} />
            <span className="text-sm font-medium">{t.preferencesSaved}</span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Sidebar Tabs */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden h-fit">
          <div className="p-2 space-y-1">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/20'
                    : 'text-slate-400 hover:bg-slate-700 hover:text-slate-200'
                }`}
              >
                <tab.icon size={18} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content Area */}
        <div className="md:col-span-3 space-y-6">
          
          {/* General Settings */}
          {activeTab === 'general' && (
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-6 animate-in fade-in slide-in-from-right-4">
              <div className="flex items-center gap-3 border-b border-slate-700 pb-4">
                <User className="text-indigo-400" size={24} />
                <h2 className="text-lg font-bold text-white">{t.accountInfo}</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                 <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">{t.email}</label>
                    <div className="relative">
                       <Mail className="absolute left-3 top-2.5 text-slate-500 w-4 h-4" />
                       <input 
                          type="email" 
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="w-full bg-slate-900 border border-slate-700 rounded-lg pl-10 pr-4 py-2 text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                       />
                    </div>
                 </div>
                 
                 <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">{t.displayName}</label>
                    <div className="relative">
                       <User className="absolute left-3 top-2.5 text-slate-500 w-4 h-4" />
                       <input 
                          type="text" 
                          defaultValue={userRole === 'PROJECT' ? 'SuperNova Protocol' : 'Crypto Wizard'}
                          className="w-full bg-slate-900 border border-slate-700 rounded-lg pl-10 pr-4 py-2 text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                       />
                    </div>
                 </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">{t.language}</label>
                  <button 
                    onClick={onLanguageChange}
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white flex justify-between items-center hover:bg-slate-800 transition-colors"
                  >
                    <span className="flex items-center gap-2">
                        <Globe size={16} className="text-slate-400" />
                        {LANGUAGE_NAMES[language]}
                    </span>
                    <span className="text-xs bg-slate-700 px-2 py-0.5 rounded text-slate-300">Change</span>
                  </button>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">{t.currency}</label>
                  <select 
                     value={currency}
                     onChange={(e) => setCurrency(e.target.value)}
                     className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                  >
                     <option value="USDT">USDT (Tether)</option>
                     <option value="USDC">USDC (USD Coin)</option>
                     <option value="ETH">ETH (Ethereum)</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Wallet Settings */}
          {activeTab === 'wallet' && (
             <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-6 animate-in fade-in slide-in-from-right-4">
               <div className="flex items-center gap-3 border-b border-slate-700 pb-4">
                 <Wallet className="text-emerald-400" size={24} />
                 <h2 className="text-lg font-bold text-white">{t.walletSettings}</h2>
               </div>

               <div className="bg-slate-900/50 p-6 rounded-xl border border-slate-700 flex flex-col md:flex-row justify-between items-center gap-4">
                  <div className="flex items-center gap-4">
                     <div className={`w-12 h-12 rounded-full flex items-center justify-center ${walletConnected ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-700 text-slate-500'}`}>
                        <CreditCard size={24} />
                     </div>
                     <div>
                        <p className="text-sm text-slate-400">{t.connectedWallet}</p>
                        <p className="text-lg font-mono font-bold text-white">
                           {walletConnected ? '0x71C...39A2' : 'Not Connected'}
                        </p>
                        {walletConnected && (
                           <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium bg-emerald-500/10 text-emerald-400 mt-1">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                              Connected
                           </span>
                        )}
                     </div>
                  </div>
                  
                  {walletConnected ? (
                     <button 
                        onClick={onDisconnectWallet}
                        className="px-4 py-2 bg-rose-500/10 text-rose-400 hover:bg-rose-500/20 border border-rose-500/30 rounded-lg flex items-center gap-2 transition-colors"
                     >
                        <LogOut size={16} /> {t.disconnectWallet}
                     </button>
                  ) : (
                     <button 
                        onClick={onConnectWallet}
                        className="px-4 py-2 bg-indigo-600 text-white hover:bg-indigo-700 rounded-lg transition-colors"
                     >
                        {t.connectWallet}
                     </button>
                  )}
               </div>

               <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">{t.network}</label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                     <div className="border border-indigo-500 bg-indigo-500/10 p-3 rounded-lg flex items-center gap-3 cursor-pointer">
                        <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                        <span className="text-white font-medium">BSC Mainnet</span>
                     </div>
                     <div className="border border-slate-700 bg-slate-900 p-3 rounded-lg flex items-center gap-3 cursor-pointer opacity-50 hover:opacity-100 transition-opacity">
                        <div className="w-3 h-3 rounded-full bg-blue-400"></div>
                        <span className="text-slate-300 font-medium">Ethereum</span>
                     </div>
                     <div className="border border-slate-700 bg-slate-900 p-3 rounded-lg flex items-center gap-3 cursor-pointer opacity-50 hover:opacity-100 transition-opacity">
                        <div className="w-3 h-3 rounded-full bg-purple-400"></div>
                        <span className="text-slate-300 font-medium">Polygon</span>
                     </div>
                  </div>
               </div>
             </div>
          )}

          {/* Notifications Settings */}
          {activeTab === 'notifications' && (
             <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-6 animate-in fade-in slide-in-from-right-4">
                <div className="flex items-center gap-3 border-b border-slate-700 pb-4">
                  <Bell className="text-yellow-400" size={24} />
                  <h2 className="text-lg font-bold text-white">{t.notificationSettings}</h2>
                </div>

                <div className="space-y-4">
                   <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-lg border border-slate-700">
                      <div>
                         <h3 className="text-white font-medium">{t.emailNotifications}</h3>
                         <p className="text-xs text-slate-400">Receive updates about your orders via email</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" checked={notifications.email} onChange={() => setNotifications({...notifications, email: !notifications.email})} className="sr-only peer" />
                        <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                      </label>
                   </div>

                   {/* Telegram Integration */}
                   <div className="p-4 bg-slate-900/50 rounded-lg border border-slate-700 space-y-3">
                      <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                              <div className="bg-sky-500/20 p-2 rounded-lg">
                                  <Send size={18} className="text-sky-400" />
                              </div>
                              <div>
                                  <h3 className="text-white font-medium">{t.telegramSettings}</h3>
                                  <p className="text-xs text-slate-400">Receive order updates instantly via Telegram Bot</p>
                              </div>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                              <input type="checkbox" checked={notifications.telegram} onChange={() => setNotifications({...notifications, telegram: !notifications.telegram})} className="sr-only peer" />
                              <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-sky-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-sky-500"></div>
                          </label>
                      </div>
                      
                      {notifications.telegram && (
                          <div className="flex gap-2 items-center animate-in fade-in slide-in-from-top-2 pt-2">
                              <input 
                                  type="text" 
                                  value={telegramHandle}
                                  onChange={(e) => setTelegramHandle(e.target.value)}
                                  placeholder={t.telegramHandle}
                                  className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-sky-500"
                              />
                              <button className="px-3 py-2 bg-sky-600 hover:bg-sky-700 text-white rounded-lg text-sm font-medium transition-colors">
                                  {telegramHandle ? t.telegramConnected : t.connectBot}
                              </button>
                          </div>
                      )}
                   </div>

                   <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-lg border border-slate-700">
                      <div>
                         <h3 className="text-white font-medium">{t.pushNotifications}</h3>
                         <p className="text-xs text-slate-400">Browser notifications for real-time alerts</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" checked={notifications.push} onChange={() => setNotifications({...notifications, push: !notifications.push})} className="sr-only peer" />
                        <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                      </label>
                   </div>

                   <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-lg border border-slate-700">
                      <div>
                         <h3 className="text-white font-medium">{t.marketingEmails}</h3>
                         <p className="text-xs text-slate-400">News, feature updates, and promotions</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" checked={notifications.marketing} onChange={() => setNotifications({...notifications, marketing: !notifications.marketing})} className="sr-only peer" />
                        <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                      </label>
                   </div>
                </div>
             </div>
          )}

          {/* Security Settings */}
          {activeTab === 'security' && (
             <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-6 animate-in fade-in slide-in-from-right-4">
                <div className="flex items-center gap-3 border-b border-slate-700 pb-4">
                  <Shield className="text-rose-400" size={24} />
                  <h2 className="text-lg font-bold text-white">{t.securitySettings}</h2>
                </div>

                <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-lg border border-slate-700">
                   <div>
                      <h3 className="text-white font-medium">{t.twoFactorAuth}</h3>
                      <p className="text-xs text-slate-400">Protect your account with an extra layer of security</p>
                   </div>
                   <label className="relative inline-flex items-center cursor-pointer">
                     <input type="checkbox" checked={twoFactor} onChange={() => setTwoFactor(!twoFactor)} className="sr-only peer" />
                     <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
                   </label>
                </div>

                <div className="p-4 bg-slate-900/50 rounded-lg border border-slate-700">
                   <h3 className="text-white font-medium mb-4">{t.changePassword}</h3>
                   <div className="space-y-3">
                      <input type="password" placeholder="Current Password" className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white text-sm focus:outline-none" disabled />
                      <input type="password" placeholder="New Password" className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white text-sm focus:outline-none" disabled />
                      <button className="text-sm text-indigo-400 hover:text-indigo-300 font-medium">Update Password</button>
                   </div>
                </div>
             </div>
          )}

          <div className="flex justify-end pt-4">
             <button 
               onClick={handleSave}
               className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-lg font-bold shadow-lg shadow-emerald-500/20 transition-all flex items-center gap-2"
             >
               <Save size={18} />
               {t.savePreferences}
             </button>
          </div>
        </div>
      </div>
    </div>
  );
};
